import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import React from "react";
import { useTranslation } from "react-i18next";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { Kanban } from "../../../model/kanban/Kanban";
import { KanbanClosingReason } from "../../../model/kanban/types/KanbanClosingReason";
import { KanbanColumnStageType } from "../../../model/kanban/types/KanbanColumnStageType.enum";
import { TestIDs } from "../../../tests/types/testIds";
import { Tooltip } from "../../../utils/local-antd";
import { getTranslatedKanbanColumnName } from "../KanbanUtils";

const MAX_REASONS_PER_STAGE = 20;

type KanbanClosingReasonsSettingsProps = {
  kanban: Kanban;
  value: KanbanClosingReason[];
  onChange: (reasons: KanbanClosingReason[]) => void;
};

export const KanbanClosingReasonsSettings: React.FC<
  KanbanClosingReasonsSettingsProps
> = ({ kanban, value, onChange }) => {
  const { t } = useTranslation();

  const stages = Object.values(KanbanColumnStageType).map((stageType) => ({
    stageType,
    name:
      getTranslatedKanbanColumnName(
        kanban.columns?.find((c) => c.stageType === stageType)?.name,
        kanban.template,
      ) || stageType,
  }));

  const onEdit = (index: number, label: string) =>
    onChange(value.map((r, i) => (i === index ? { ...r, label } : r)));

  const onRemove = (index: number) =>
    onChange(value.filter((_, i) => i !== index));

  const onAdd = (stageType: KanbanColumnStageType) =>
    onChange([...value, { stageType, label: "" }]);

  return (
    <Flex flexDirection="column" gap="m" marginTop="m" width="100%">
      <Text fontWeight="bold">{t("kanban.settings.closingReasons.title")}</Text>
      <Info info={t("kanban.settings.closingReasons.info")} />

      {stages.map(({ stageType, name }) => {
        const rows = value
          .map((reason, index) => ({ reason, index }))
          .filter(({ reason }) => reason.stageType === stageType);

        return (
          <Flex key={stageType} flexDirection="column" gap="s" width="100%">
            <Text color="main">{name}</Text>

            {rows.map(({ reason, index }, position) => (
              <Flex key={index} alignItems="center" gap="s" width="100%">
                <Input
                  value={reason.label}
                  onChange={(e) => onEdit(index, e.currentTarget.value)}
                  placeholder={t(
                    "kanban.settings.closingReasons.input.placeholder",
                  )}
                  dataTestId={TestIDs.kanbans.settings.closingReasons.input(
                    stageType,
                    position,
                  )}
                />
                <Tooltip title={t("common.delete")}>
                  <Button
                    backgroundColor="transparent"
                    margin="none"
                    padding="s"
                    onClick={() => onRemove(index)}
                    dataTestId={TestIDs.kanbans.settings.closingReasons.delete(
                      stageType,
                      position,
                    )}
                  >
                    <AtiraIcon icon={faTrash} color="danger" />
                  </Button>
                </Tooltip>
              </Flex>
            ))}

            {rows.length < MAX_REASONS_PER_STAGE ? (
              <Button
                backgroundColor="transparent"
                color="main"
                margin="none"
                onClick={() => onAdd(stageType)}
                dataTestId={TestIDs.kanbans.settings.closingReasons.add(
                  stageType,
                )}
              >
                <Flex alignItems="center" gap="s">
                  <AtiraIcon icon={faPlus} color="main" />
                  <Text color="main">
                    {t("kanban.settings.closingReasons.add")}
                  </Text>
                </Flex>
              </Button>
            ) : null}
          </Flex>
        );
      })}
    </Flex>
  );
};
